// ── SupplierDirectory — 공급처 마스터 관리 페이지 (V18, Admin 전용) + V21 통계 ──
import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../db';
import {
  SUPPLIER_CATEGORIES, PAYMENT_TERMS, SUPPLIER_STATUS,
  emptySupplier, subscribeSuppliers,
  addSupplier, updateSupplier, deleteSupplier,
  filterSuppliers, sortSuppliers, categoryLabel,
} from '../utils/suppliers';
import { calcSavings } from '../utils/groupBuying';

const fmt = (n) => (Number(n) || 0).toLocaleString('ko-KR');

export default function SupplierDirectory({ user, isAdminUser }) {
  const [suppliers, setSuppliers] = useState([]);
  const [groupBuyings, setGroupBuyings] = useState([]); // V21: 안건 통계용
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    const unsub = subscribeSuppliers(
      (list) => { setSuppliers(list); setLoading(false); },
      (err) => { console.warn('suppliers load error:', err.message); setLoading(false); }
    );
    return unsub;
  }, []);

  // V21: 공동구매 안건 구독 (공급처별 통계용)
  useEffect(() => {
    const q = query(collection(db, 'groupBuyings'));
    const unsub = onSnapshot(q,
      (snap) => setGroupBuyings(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      (err) => console.warn('groupBuyings load (stats):', err.message)
    );
    return unsub;
  }, []);

  // V21: 공급처별 통계
  const supplierStats = useMemo(() => {
    const m = new Map();
    for (const gb of (groupBuyings || [])) {
      if (!gb.supplierId) continue;
      const cur = m.get(gb.supplierId) || { dealCount: 0, totalSavings: 0, openCount: 0 };
      cur.dealCount += 1;
      const sav = calcSavings(gb);
      cur.totalSavings += (sav.amount || 0);
      if (gb.status === 'open') cur.openCount += 1;
      m.set(gb.supplierId, cur);
    }
    return m;
  }, [groupBuyings]);

  const visible = useMemo(() => {
    const filtered = filterSuppliers(suppliers, { q: search, category: filterCat, status: filterStatus });
    return sortSuppliers(filtered);
  }, [suppliers, search, filterCat, filterStatus]);

  const stats = useMemo(() => {
    const total = suppliers.length;
    const active = suppliers.filter(s => s.status === 'active').length;
    const categoriesUsed = new Set();
    suppliers.forEach(s => (s.categories || []).forEach(c => categoriesUsed.add(c)));
    return { total, active, catCount: categoriesUsed.size };
  }, [suppliers]);

  if (!isAdminUser) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
        🔒 운영자 전용 기능입니다.
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>🏪 공급처 관리</h2>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6b7280' }}>
            전체 {stats.total} · 활성 {stats.active} · 카테고리 {stats.catCount}종
          </p>
        </div>
        <button onClick={() => setEditing(emptySupplier())}
          style={{ padding: '10px 16px', borderRadius: 8, background: '#FF6F0F', color: '#fff', border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          + 공급처 등록
        </button>
      </div>

      {/* 검색·필터 */}
      <div style={{ background: '#fff', borderRadius: 10, padding: 12, marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="이름·담당자·전화·지역 검색"
          style={{ flex: '1 1 200px', minWidth: 0, padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13 }} />
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
          style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13 }}>
          <option value="">전체 카테고리</option>
          {SUPPLIER_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13 }}>
          <option value="">전체 상태</option>
          {Object.entries(SUPPLIER_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* 목록 */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>로딩 중...</div>
      ) : visible.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', background: '#fff', borderRadius: 10 }}>
          {suppliers.length === 0 ? '등록된 공급처가 없습니다. "+ 공급처 등록"으로 추가하세요.' : '검색 결과가 없습니다.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {visible.map(s => (
            <SupplierCard key={s.id} supplier={s}
              stats={supplierStats.get(s.id)}
              onEdit={() => setEditing(s)} />
          ))}
        </div>
      )}

      {/* 등록/수정 모달 */}
      {editing && (
        <SupplierEditor supplier={editing} user={user}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)} />
      )}
    </div>
  );
}

// ── 공급처 카드 ──
function SupplierCard({ supplier, stats, onEdit }) {
  const st = SUPPLIER_STATUS[supplier.status] || SUPPLIER_STATUS.inactive;
  const cats = (supplier.categories || []).slice(0, 3);
  const moreCount = (supplier.categories || []).length - cats.length;

  return (
    <div style={{ background: '#fff', borderRadius: 10, padding: 14, border: '1px solid #e5e7eb' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontWeight: 700, fontSize: 15 }}>{supplier.name || '(이름 없음)'}</span>
            <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: st.bg, color: st.color, fontWeight: 700 }}>
              {st.label}
            </span>
            {supplier.rating > 0 && (
              <span style={{ fontSize: 11, color: '#A1A1AA' }}>★ {supplier.rating.toFixed(1)}</span>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            {supplier.contactName && <span>{supplier.contactName} · </span>}
            {supplier.phone && <span>📞 {supplier.phone} · </span>}
            {supplier.region && <span>📍 {supplier.region}</span>}
          </div>
        </div>
        <button onClick={onEdit}
          style={{ padding: '6px 12px', borderRadius: 6, background: 'none', border: '1px solid #d1d5db', fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>
          편집
        </button>
      </div>
      {cats.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
          {cats.map(c => (
            <span key={c} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#f3f4f6', color: '#374151' }}>
              {categoryLabel(c)}
            </span>
          ))}
          {moreCount > 0 && (
            <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#f3f4f6', color: '#9ca3af' }}>
              +{moreCount}
            </span>
          )}
        </div>
      )}
      {(supplier.minOrder > 0 || supplier.deliveryFee > 0) && (
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
          {supplier.minOrder > 0 && <span>최소발주 {fmt(supplier.minOrder)}원 </span>}
          {supplier.deliveryFee > 0 && <span>· 배송 {fmt(supplier.deliveryFee)}원</span>}
        </div>
      )}

      {/* V21: 안건 통계 */}
      {stats && stats.dealCount > 0 && (
        <div style={{
          display: 'flex', gap: 8, marginTop: 8, paddingTop: 8,
          borderTop: '1px dashed #e5e7eb', fontSize: 11, flexWrap: 'wrap',
        }}>
          <span style={{ color: '#374151' }}>
            🛒 함께한 안건 <strong>{stats.dealCount}건</strong>
          </span>
          {stats.openCount > 0 && (
            <span style={{ color: '#18181B', fontWeight: 600 }}>
              · 진행 중 {stats.openCount}
            </span>
          )}
          {stats.totalSavings > 0 && (
            <span style={{ color: '#18181B', fontWeight: 600 }}>
              · 절감 합계 ₩{fmt(stats.totalSavings)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ── 공급처 등록/수정 모달 ──
function SupplierEditor({ supplier, user, onClose, onSaved }) {
  const [form, setForm] = useState({ ...emptySupplier(), ...supplier });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isNew = !supplier.id;

  const update = (patch) => setForm(prev => ({ ...prev, ...patch }));
  const toggleCategory = (cat) => {
    const cur = form.categories || [];
    update({ categories: cur.includes(cat) ? cur.filter(c => c !== cat) : [...cur, cat] });
  };

  const handleSave = async () => {
    if (!form.name?.trim()) { setError('공급처 이름은 필수입니다.'); return; }
    setSaving(true);
    setError('');
    try {
      if (isNew) {
        await addSupplier(form, user?.uid);
      } else {
        const { id, createdAt, createdBy, ...patch } = form;
        await updateSupplier(supplier.id, patch);
      }
      onSaved();
    } catch (e) {
      setError(e.message || '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!supplier.id) return;
    if (!window.confirm(`"${supplier.name}" 공급처를 삭제하시겠습니까?`)) return;
    setSaving(true);
    try {
      await deleteSupplier(supplier.id);
      onSaved();
    } catch (e) {
      setError(e.message || '삭제 실패');
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 14, width: 560, maxWidth: '100%', maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #eee', background: '#f9fafb' }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{isNew ? '🏪 공급처 등록' : '🏪 공급처 수정'}</div>
        </div>
        <div style={{ padding: 18, overflow: 'auto', flex: 1 }}>
          <Field label="이름 *">
            <input type="text" value={form.name} onChange={e => update({ name: e.target.value })}
              style={fieldStyle} placeholder="예: 강남식자재" />
          </Field>
          <Field label="담당자명">
            <input type="text" value={form.contactName} onChange={e => update({ contactName: e.target.value })}
              style={fieldStyle} placeholder="예: 김사장" />
          </Field>
          <div style={{ display: 'flex', gap: 8 }}>
            <Field label="전화" flex>
              <input type="text" value={form.phone} onChange={e => update({ phone: e.target.value })}
                style={fieldStyle} placeholder="010-1234-5678" />
            </Field>
            <Field label="카카오톡" flex>
              <input type="text" value={form.kakao} onChange={e => update({ kakao: e.target.value })}
                style={fieldStyle} placeholder="@id 또는 오픈채팅" />
            </Field>
          </div>
          <Field label="이메일">
            <input type="email" value={form.email} onChange={e => update({ email: e.target.value })}
              style={fieldStyle} placeholder="optional" />
          </Field>
          <Field label="사업자등록번호">
            <input type="text" value={form.bizNumber} onChange={e => update({ bizNumber: e.target.value })}
              style={fieldStyle} placeholder="000-00-00000" />
          </Field>

          <Field label="취급 카테고리">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {SUPPLIER_CATEGORIES.map(c => {
                const selected = (form.categories || []).includes(c.value);
                return (
                  <button key={c.value} type="button" onClick={() => toggleCategory(c.value)}
                    style={{
                      padding: '6px 10px', borderRadius: 6, fontSize: 12,
                      border: '1px solid', borderColor: selected ? '#FF6F0F' : '#e5e7eb',
                      background: selected ? '#FF6F0F' : '#fff',
                      color: selected ? '#fff' : '#374151',
                      cursor: 'pointer', fontWeight: 600,
                    }}>
                    {c.label}
                  </button>
                );
              })}
            </div>
          </Field>

          <div style={{ display: 'flex', gap: 8 }}>
            <Field label="지역" flex>
              <input type="text" value={form.region} onChange={e => update({ region: e.target.value })}
                style={fieldStyle} placeholder="예: 서울 송파" />
            </Field>
            <Field label="결제 조건" flex>
              <select value={form.paymentTerms} onChange={e => update({ paymentTerms: e.target.value })}
                style={fieldStyle}>
                {PAYMENT_TERMS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="주소 (상세)">
            <input type="text" value={form.address} onChange={e => update({ address: e.target.value })}
              style={fieldStyle} placeholder="optional" />
          </Field>

          <div style={{ display: 'flex', gap: 8 }}>
            <Field label="최소 발주 금액 (원)" flex>
              <input type="number" value={form.minOrder || ''} onChange={e => update({ minOrder: Number(e.target.value) || 0 })}
                style={{ ...fieldStyle, textAlign: 'right' }} placeholder="0" />
            </Field>
            <Field label="배송비 (원)" flex>
              <input type="number" value={form.deliveryFee || ''} onChange={e => update({ deliveryFee: Number(e.target.value) || 0 })}
                style={{ ...fieldStyle, textAlign: 'right' }} placeholder="0" />
            </Field>
          </div>

          <Field label="메모">
            <textarea value={form.notes} onChange={e => update({ notes: e.target.value })}
              rows={3} style={{ ...fieldStyle, resize: 'vertical', fontFamily: 'inherit' }}
              placeholder="협상 메모, 특이사항, 단골 조건 등" />
          </Field>

          <div style={{ display: 'flex', gap: 8 }}>
            <Field label="상태" flex>
              <select value={form.status} onChange={e => update({ status: e.target.value })}
                style={fieldStyle}>
                {Object.entries(SUPPLIER_STATUS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </Field>
            <Field label="평점 (0~5)" flex>
              <input type="number" min="0" max="5" step="0.1"
                value={form.rating || ''} onChange={e => update({ rating: Number(e.target.value) || 0 })}
                style={{ ...fieldStyle, textAlign: 'right' }} placeholder="0" />
            </Field>
          </div>

          {error && (
            <div style={{ background: '#F4F4F5', color: '#52525B', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginTop: 8 }}>
              {error}
            </div>
          )}
        </div>

        <div style={{ padding: '12px 18px', borderTop: '1px solid #eee', display: 'flex', gap: 8, justifyContent: 'space-between', background: '#f9fafb' }}>
          {!isNew && (
            <button onClick={handleDelete} disabled={saving}
              style={{ padding: '9px 16px', borderRadius: 7, background: '#fff', color: '#52525B', border: '1px solid #fca5a5', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              삭제
            </button>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button onClick={onClose} disabled={saving}
              style={{ padding: '9px 16px', borderRadius: 7, background: '#fff', border: '1px solid #d1d5db', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              취소
            </button>
            <button onClick={handleSave} disabled={saving}
              style={{ padding: '9px 18px', borderRadius: 7, background: '#FF6F0F', color: '#fff', border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              {saving ? '저장 중...' : '저장'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const fieldStyle = {
  width: '100%', padding: '8px 12px', border: '1px solid #d1d5db',
  borderRadius: 6, fontSize: 13, boxSizing: 'border-box', background: '#fff',
};

function Field({ label, children, flex }) {
  return (
    <label style={{ display: 'block', marginBottom: 10, flex: flex ? 1 : undefined }}>
      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4, fontWeight: 600 }}>{label}</div>
      {children}
    </label>
  );
}
